<script lang="ts" setup>

	import {useI18n} from 'vue-i18n';
	import {computed, ref} from 'vue';
	import {
		containsPoinsettiasWithPF671,
		type DeliveryDate,
		getTotalAmountOfCC,
		getTotalAmountOfTrays,
		getTotalAmountOfVarieties,
		setSelectedWeekAndScroll,
		type ShoppingCartOrderLine
	} from 'dummen-shared';
	import AddProductsModal from '@/components/modals/AddProductsModal.vue';
	import DeleteWeekModal from '@/components/modals/DeleteWeekModal.vue';
	import ShoppingcartNotice from '@/components/ShoppingcartNotice.vue';

	const emit = defineEmits(['on-close-shopping-cart']);

	const props = defineProps<{
		week: number;
		year: number;
		isActiveWeek: boolean;
		orderLines: ShoppingCartOrderLine[];
		showInvalidOrderLineWarning: boolean;
	}>();

	const {t} = useI18n();

	type AvailabilityWindow = Window & {
		syngentaTenant?: boolean;
	};

	const availabilityWindow = window as AvailabilityWindow;
	const isSyngentaTenant = computed(() => availabilityWindow.syngentaTenant === true);

	const showAddMoreProductsModal = ref(false);
	const showDeleteWeekModal = ref(false);

	const totalVarieties = computed(() => getTotalAmountOfVarieties(props.orderLines));

	const totalProducts = computed(() => {
		if (!props.orderLines?.length) {
			return 0;
		}

		return props.orderLines
			.map(line => line.amount)
			.reduce((sum, amount) => {
				return sum + amount;
			}, 0);
	});

	const totalTrays = computed(() => getTotalAmountOfTrays((props.orderLines)));

	const totalTrolleys = computed(() => getTotalAmountOfCC(props.orderLines));

	const showTrayDisclaimer = computed(() => {
		return totalTrays.value && containsPoinsettiasWithPF671(props.orderLines);
	});

	const addMoreProductsToDeliveryDate = (deliveryDate: DeliveryDate) => {
		let week: number | string = deliveryDate.week;

		// The product overview always expects a 2 digit string (e.g. 05 instead of 5).
		if (week < 10) {
			week = '0' + week;
		}

		const orderWeek = deliveryDate.year + '' + week;

		setSelectedWeekAndScroll(orderWeek);

		closeAddMoreProductsModal();
		emit('on-close-shopping-cart');

		window.location.href = window.catalogInOrderModeUrl;
	};

	const openAddMoreProductsModal = () => showAddMoreProductsModal.value = true;

	const closeAddMoreProductsModal = () => showAddMoreProductsModal.value = false;

	const openDeleteWeekModal = () => showDeleteWeekModal.value = true;

	const closeDeleteWeekModal = () => showDeleteWeekModal.value = false;

</script>

<template>
	<div class="shopping-cart-summary">
		<h3 :title="`${t('shoppingcart.generic.week.text')} ${week} - ${year}`">
			{{ t('shoppingcart.generic.week.text') }} {{ week }} - {{ year }}
		</h3>

		<div class="shopping-cart-week-summary">
			<div class="summary-data">
				<div class="summary-action-buttons">
					<button class="button button--orange button--block button--small"
					        @click.prevent="openAddMoreProductsModal">

						<svg class="icon-add-products" height="14" width="14">
							<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#add-products"></use>
						</svg>

						{{ t('shoppingcart.addProducts.text') }}
					</button>

					<button class="button button--orange button--block button--small"
					        @click.prevent="openDeleteWeekModal">

						<svg class="icon-copy-po" height="16" width="14">
							<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#delete-po"></use>
						</svg>

						{{ t('shoppingcart.deleteWeek.text') }}
					</button>
				</div>

				<div class="summary-week-stats">
					<div>
						<div class="summary-data-label">
							{{ t('shoppingcart.totalTrays.label') }}
						</div>

						<div class="summary-data-value">
							{{ totalTrays || '-' }}
						</div>
					</div>

					<div v-if="!isSyngentaTenant">
						<div class="summary-data-label">
							{{ t('shoppingcart.totalTrolleys.label') }}
						</div>

						<div class="summary-data-value">
							{{ totalTrolleys || '-' }}
						</div>
					</div>

					<div>
						<div class="summary-data-label">
							{{ t('shoppingcart.totalVarieties.label') }}
						</div>

						<div class="summary-data-value">
							{{ totalVarieties }}
						</div>
					</div>

					<div>
						<div class="summary-data-label">
							{{ t('shoppingcart.totalProducts.label') }}
						</div>

						<div class="summary-data-value">
							{{ totalProducts }}
						</div>
					</div>
				</div>
			</div>

			<div class="summary-notice-container">
				<shoppingcart-notice v-if="showTrayDisclaimer">
					{{ t('shoppingcart.orderContainsPoinsettias.message') }}
				</shoppingcart-notice>

				<shoppingcart-notice v-if="showInvalidOrderLineWarning">
					{{ t('shoppingcart.weekContainsInvalidOrderLines.message') }}
				</shoppingcart-notice>
			</div>
		</div>
	</div>

	<teleport to="#shoppingcart-modal-container">
		<add-products-modal v-if="showAddMoreProductsModal"
		                    :week="week"
		                    :year="year"
		                    @close="closeAddMoreProductsModal"
		                    @confirm="addMoreProductsToDeliveryDate" />

		<delete-week-modal v-if="showDeleteWeekModal"
		                   :week="week"
		                   :year="year"
		                   @close="closeDeleteWeekModal" />
	</teleport>
</template>

<style lang="scss" scoped>
	@use "sass:math";
	@import '../../../../../../src/main/webapp/template/assets/css/source/settings';

	.shopping-cart-summary {
		border: 2px solid #eef0f2;
		border-bottom: none;

		h3 {
			margin: 0;
			padding: 15px 15px 0;
			font-weight: 600;
			background: white;
		}

		.summary-data {
			display: flex;
			align-items: center;
			padding: 15px 55px;
			font-size: math.div(12, 16)*1rem;

			.summary-week-stats {
				display: flex;
				flex: 1;
				justify-content: space-between;
				padding-left: 55px;

				.summary-data-label {
					margin-bottom: 10px;
				}

				.summary-data-value {
					font-weight: 600;
				}
			}

			@media #{$mq-large-only} {
				font-size: 1rem;
				line-height: math.div(18, 14)*1em;
			}
		}

		.summary-action-buttons {
			button {
				font-size: .75em;

				&:not(:last-child) {
					margin-bottom: 8px;
				}
			}
		}

		.summary-notice-container {
			text-align: right;
		}
	}
</style>
